// web equivalent of native's uikit-react-native.ReactionAddons (existing-reactions strip + long-press emoji bottom sheet).
// thin pass-throughs around stock @sendbird/uikit-react components.
import type { GroupChannel } from "@sendbird/chat/groupChannel";
import type {
  FileMessage,
  MultipleFilesMessage,
  UserMessage,
} from "@sendbird/chat/message";
import { useSendbird } from "@sendbird/uikit-react";
import EmojiReactions from "@sendbird/uikit-react/ui/EmojiReactions";
import { MobileBottomSheet } from "@sendbird/uikit-react/ui/MobileMenu";

import { useAppUserSelector } from "@/store/reducers/appUser";

// Mirrors @sendbird/uikit-react's internal SendableMessageType (not a public export).
type SendableMessage = FileMessage | MultipleFilesMessage | UserMessage;

const buildToggleReaction =
  (channel: GroupChannel) =>
  // eslint-disable-next-line max-params -- mirrors @sendbird/uikit-react's toggleReaction(message, emojiKey, isReacted) contract
  (message: SendableMessage, emojiKey: string, isReacted: boolean) => {
    void (isReacted
      ? channel.deleteReaction(message, emojiKey)
      : channel.addReaction(message, emojiKey));
  };

interface MessageProps {
  channel: GroupChannel;
  message: SendableMessage;
}

const Message = ({ channel, message }: MessageProps) => {
  const {
    state: { emojiManager },
  } = useSendbird();
  const { externalId: appUserId } = useAppUserSelector();

  // existing-reactions strip; sendbird's <EmojiReactions> handles its own toggleReaction via channel.add/deleteReaction
  return (
    <EmojiReactions
      channel={channel}
      emojiContainer={emojiManager.emojiContainer}
      memberNicknamesMap={new Map()}
      message={message}
      toggleReaction={buildToggleReaction(channel)}
      userId={appUserId}
    />
  );
};

interface BottomSheetProps {
  channel: GroupChannel;
  message: SendableMessage;
  onClose: () => Promise<void>;
}

// stock MobileBottomSheet renders the emoji reaction bar AND a list of action items (Copy/Edit/Reply/Delete/etc).
// our mobile UX wants only the emoji bar; renderMenuItems must return a TRUTHY ReactNode (e.g. <></>) to suppress the actions —
// returning null falls through to sendbird's defaults (MobileMenu.js uses `renderMenuItems?.(...) ?? defaults`, and null ?? x === x).
const BottomSheet = ({ channel, message, onClose }: BottomSheetProps) => {
  const {
    state: { emojiManager },
  } = useSendbird();
  const { externalId: appUserId } = useAppUserSelector();

  return (
    <MobileBottomSheet
      channel={channel}
      emojiContainer={emojiManager.emojiContainer}
      hideMenu={() => {
        void onClose();
      }}
      isReactionEnabled
      message={message}
      // an empty fragment here is necessary, as returning null will cause sendbird to fallback to its stock menu (WITH Copy/Edit/Reply/Delete)
      // eslint-disable-next-line react/jsx-no-useless-fragment
      renderMenuItems={() => <></>}
      toggleReaction={buildToggleReaction(channel)}
      userId={appUserId}
    />
  );
};

const ReactionAddons = {
  BottomSheet,
  Message,
};

export { ReactionAddons };
